import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('dj-language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('dj-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      team: "Équipe",
      contact: "Contact",
      information: "Information",
      blog: "Blog",
      quote: "Soumission gratuite"
    },
    hero: {
      badge: "Ingénieurs accrédités OIQ et OIO",
      title: "L'excellence en ingénierie structurale",
      subtitle: "De la conception à la réalisation, nous transformons vos projets en structures solides et durables",
      cta: "Demander une soumission",
      secondaryCta: "Découvrir nos services"
    },
    about: {
      sectionTitle: "À propos de nous",
      title: "Votre partenaire de confiance en ingénierie au Québec et Ontario",
      description: "D&J Engineering est une firme d'ingénierie spécialisée en structure du bâtiment au Québec et en Ontario. Notre équipe d'ingénieurs accrédités combine expertise technique, innovation et rigueur professionnelle pour offrir des solutions adaptées à chaque projet.",
      highlight: "Ingénieurs du Québec et d'Ontario accrédités avec sceau professionnel",
      stats: {
        experience: "Années d'expérience",
        projects: "Projets réalisés",
        satisfaction: "Satisfaction client",
        members: "Membres accrédités"
      }
    },
    services: {
      sectionTitle: "Nos services",
      title: "Des solutions complètes en ingénierie structurale",
      subtitle: "De la conception à la réalisation, nous offrons une gamme complète de services adaptés à vos besoins.",
      viewAll: "Voir tous nos services",
      pageTitle: "Nos Services",
      pageSubtitle: "Une gamme complète de services en ingénierie structurale pour accompagner tous vos projets au Québec et en Ontario.",
      items: {
        structural: {
          title: "Conception structurelle",
          shortDesc: "Dessins structurels conformes aux normes québécoises et ontariennes",
          fullDesc: "Notre expertise en conception structurelle couvre les modifications, rénovations et agrandissements. Chaque projet est réalisé dans le respect des normes et exigences réglementaires en vigueur au Québec et en Ontario.",
          features: [
            "Les nouvelles constructions",
            "Dessins structurels (modifications, rénovations, agrandissements)",
            "Respect des normes et exigences réglementaires",
            "Ingénieur du Québec et d'Ontario accrédité avec sceau professionnel",
            "Collaboration avec entrepreneurs, architectes et propriétaires",
            "Accent sur sécurité, durabilité et efficacité"
          ]
        },
        estimation: {
          title: "Estimation structurale",
          shortDesc: "Estimations précises grâce à notre expertise et à la technologie BIM",
          fullDesc: "Nous offrons une expertise pointue en estimation de bâtiments, que ce soit pour des constructions neuves, des rénovations ou des agrandissements. Notre approche combine expérience terrain et technologies de pointe.",
          features: [
            "Expertise en estimation de bâtiments (neuf, rénovation, agrandissement)",
            "Analyse détaillée des quantités",
            "Utilisation des technologies BIM pour une précision accrue",
            "Estimations fiables, réalistes et adaptées à la réalité du chantier"
          ]
        },
        management: {
          title: "Gestion de projet",
          shortDesc: "Accompagnement complet de la planification à la livraison",
          fullDesc: "Notre service de gestion de projet vous accompagne de A à Z dans la réalisation de vos travaux. Nous assurons la coordination, le suivi et la communication transparente avec toutes les parties prenantes.",
          features: [
            "Accompagnement de A à Z",
            "Planification, coordination, suivi des travaux",
            "Gestion des sous-traitants, coûts et échéanciers",
            "Communication transparente avec toutes les parties"
          ]
        },
        bim: {
          title: "Modélisation 3D (BIM)",
          shortDesc: "Modèles 3D précis pour une coordination optimale",
          fullDesc: "La modélisation BIM (Building Information Modeling) nous permet de créer des modèles 3D précis et réalistes, facilitant la coordination interdisciplinaire et la détection de conflits.",
          features: [
            "Modèles 3D précis et réalistes",
            "Coordination interdisciplinaire",
            "Détection de conflits (clash detection)",
            "Réduction des imprévus et amélioration des estimations",
            "Conformité aux normes canadiennes"
          ]
        },
        allinone: {
          title: "Solution tout-en-un",
          shortDesc: "Un seul interlocuteur pour tous vos besoins en ingénierie",
          fullDesc: "Notre solution tout-en-un combine conception, estimation et gestion de projet en un service intégré. Un seul point de contact pour une expérience fluide, du premier croquis à la remise des clés.",
          features: [
            "Conception des plans",
            "Estimation détaillée",
            "Gestion complète du projet",
            "Un seul point de contact",
            "Gain de temps, réduction des risques et qualité supérieure"
          ]
        }
      },
      process: {
        title: "Un processus éprouvé",
        subtitle: "De la première consultation à la livraison finale, nous vous accompagnons à chaque étape.",
        steps: [
          { step: "01", title: "Consultation", desc: "Analyse de vos besoins et objectifs" },
          { step: "02", title: "Conception", desc: "Développement des plans et modèles" },
          { step: "03", title: "Validation", desc: "Revue technique et approbation" },
          { step: "04", title: "Livraison", desc: "Documents finaux scellés" }
        ]
      }
    },
    whyUs: {
      sectionTitle: "Nos atouts",
      title: "Pourquoi nous choisir?",
      subtitle: "D&J Engineering se distingue par son expertise, sa rigueur et son engagement envers l'excellence.",
      items: {
        expertise: {
          title: "Expertise et innovation",
          description: "Nous utilisons les technologies de pointe, incluant le BIM, pour offrir des solutions innovantes et précises."
        },
        quality: {
          title: "Qualité irréprochable",
          description: "Chaque projet est réalisé avec la plus grande rigueur, respectant les normes canadiennes et québécoises."
        },
        support: {
          title: "Accompagnement personnalisé",
          description: "Nous adaptons nos services à vos besoins spécifiques et vous accompagnons à chaque étape."
        },
        deadline: {
          title: "Respect des délais",
          description: "Nous nous engageons à respecter les échéanciers et budgets convenus."
        },
        professionalism: {
          title: "Professionnalisme et rigueur",
          description: "Notre équipe d'ingénieurs accrédités garantit un travail professionnel et conforme."
        },
        trust: {
          title: "Partenaire de confiance",
          description: "Nous bâtissons des relations durables basées sur la confiance et la transparence."
        }
      }
    },
    cta: {
      title: "Prêt à démarrer votre projet?",
      subtitle: "Contactez-nous pour une consultation gratuite",
      button: "Nous contacter"
    },
    aboutPage: {
      badge: "Qui sommes-nous",
      title: "À propos",
      subtitle: "Découvrez l'équipe derrière D&J Engineering et notre engagement envers l'excellence en ingénierie structurale.",
      identityTitle: "Notre identité",
      teamTitle: "Une équipe polyvalente et dévouée",
      teamIntro: "D&J Engineering réunit une équipe multidisciplinaire passionnée par l'excellence en ingénierie structurale. Notre force réside dans la complémentarité de nos compétences et notre culture de collaboration.",
      values: [
        "Innovation continue et adoption des meilleures pratiques",
        "Excellence et rigueur dans chaque projet",
        "Engagement envers la qualité et la sécurité",
        "Communication transparente avec nos clients"
      ],
      meetTeam: "Rencontrer notre équipe",
      missionTitle: "Notre mission",
      missionContent: "Transformer les idées en structures solides et durables. Nous mettons notre précision, notre transparence et notre passion du détail au service de chaque projet. Par une analyse technique rigoureuse, une estimation précise grâce à notre expérience et au BIM, et une gestion proactive des projets, nous nous positionnons comme votre véritable partenaire de réussite.",
      valuesSection: {
        title: "Ce qui nous guide",
        precision: {
          title: "Précision",
          desc: "Chaque calcul, chaque dessin est réalisé avec la plus grande rigueur pour garantir la sécurité et la conformité de vos projets."
        },
        transparency: {
          title: "Transparence",
          desc: "Une communication claire et honnête à chaque étape du projet. Vous êtes informés et impliqués dans toutes les décisions."
        },
        passion: {
          title: "Passion",
          desc: "Notre passion pour l'ingénierie nous pousse à toujours chercher les meilleures solutions et à dépasser les attentes."
        }
      }
    },
    teamPage: {
      badge: "Notre équipe",
      title: "L'équipe D&J Engineering",
      subtitle: "Des professionnels passionnés et expérimentés, dédiés à l'excellence en ingénierie structurale.",
      introTitle: "Une équipe polyvalente et dévouée",
      introDesc: "Notre équipe multidisciplinaire combine expertise technique, leadership et passion pour délivrer des projets d'exception. La complémentarité de nos compétences est notre plus grande force.",
      ctaTitle: "Travaillons ensemble sur votre prochain projet",
      ctaSubtitle: "Notre équipe est prête à vous accompagner dans la réalisation de vos projets de construction.",
      members: {
        david: {
          name: "David Aluma",
          role: "Président Directeur Général & Fondateur",
          title: "",
          specialty: "Diplômé en génie civil",
          description: "Visionnaire et moteur de D&J Engineering, David apporte son expertise en conception, estimation et dessin structurel. Son leadership et sa rigueur technique ont forgé la réputation d'excellence de la firme.",
          expertise: ["Conception structurale", "Estimation", "Dessin structurel", "Leadership stratégique"]
        },
        joel: {
          name: "Joël Padingani Ing.",
          role: "Vice-Président & Associé",
          title: "Ing.",
          specialty: "Ingénieur civil",
          description: "Pilier stratégique de D&J Engineering, Joël excelle dans la planification et la gestion de projets. Son leadership et son efficacité assurent la réussite de chaque mandat.",
          expertise: ["Gestion de projet", "Planification", "Coordination", "Leadership"]
        },
        leonard: {
          name: "Léonard Ma Gloire Bila",
          role: "Consultant Principal & Expert en Construction",
          title: "",
          specialty: "Expert en construction",
          description: "Avec plus de 15 ans d'expérience dans le domaine de la construction, Léonard apporte à D&J Engineering une expertise rare et une vision stratégique forgée sur le terrain. Véritable référence dans la gestion de projet et l'analyse des coûts, il combine savoir-faire technique, rigueur et leadership inspirant.",
          expertise: ["Gestion de projet", "Analyse des coûts", "Supervision de chantier", "15+ ans d'expérience"]
        }
      }
    },
    contactPage: {
      badge: "Contactez-nous",
      title: "Parlons de votre projet",
      subtitle: "Notre équipe est à votre écoute pour répondre à vos questions et vous accompagner dans vos projets.",
      infoTitle: "Nos coordonnées",
      infoDesc: "N'hésitez pas à nous contacter pour toute question ou demande de soumission. Nous vous répondrons dans les plus brefs délais.",
      formTitle: "Envoyez-nous un message",
      formDesc: "Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.",
      labels: {
        name: "Nom complet",
        email: "Courriel",
        phone: "Téléphone",
        subject: "Sujet",
        message: "Message"
      },
      placeholders: {
        name: "Votre nom",
        email: "votre@email.com",
        phone: "(514) 555-0123",
        subject: "Objet de votre message",
        message: "Décrivez votre projet ou votre question..."
      },
      submit: "Envoyer le message",
      success: {
        title: "Message envoyé!",
        desc: "Merci pour votre message. Notre équipe vous contactera dans les plus brefs délais."
      },
      details: {
        phone: "Téléphone",
        email: "Courriel",
        location: "Localisation",
        hours: "Heures d'ouverture",
        hoursValue: "Lun - Ven: 8h00 - 17h00"
      }
    },
    footer: {
      description: "Firme d'ingénierie spécialisée en structure du bâtiment au Québec et en Ontario.",
      services: "Nos Services",
      navigation: "Navigation",
      contact: "Contact",
      rights: "Tous droits réservés.",
      oiqMember: "Ingénieurs membres de l'Ordre des ingénieurs du Québec et d'Ontario",
      certification: "Certification professionnelle"
    }
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      team: "Team",
      contact: "Contact",
      information: "Information",
      blog: "Blog",
      quote: "Free Quote"
    },
    hero: {
      badge: "OIQ and OIO Accredited Engineers",
      title: "Excellence in Structural Engineering",
      subtitle: "From design to completion, we transform your projects into solid and durable structures",
      cta: "Request a Quote",
      secondaryCta: "Discover Our Services"
    },
    about: {
      sectionTitle: "About Us",
      title: "Your Trusted Engineering Partner in Quebec and Ontario",
      description: "D&J Engineering is an engineering firm specializing in building structures in Quebec and Ontario. Our team of accredited engineers combines technical expertise, innovation, and professional rigor to provide solutions tailored to each project.",
      highlight: "Quebec and Ontario Engineers accredited with professional seal",
      stats: {
        experience: "Years of Experience",
        projects: "Completed Projects",
        satisfaction: "Client Satisfaction",
        members: "Accredited Members"
      }
    },
    services: {
      sectionTitle: "Our Services",
      title: "Complete Structural Engineering Solutions",
      subtitle: "From design to completion, we offer a complete range of services tailored to your needs.",
      viewAll: "View All Services",
      pageTitle: "Our Services",
      pageSubtitle: "A complete range of structural engineering services to support all your projects in Quebec and Ontario.",
      items: {
        structural: {
          title: "Structural Design",
          shortDesc: "Structural drawings compliant with Quebec and Ontario standards",
          fullDesc: "Our expertise in structural design covers modifications, renovations, and expansions. Each project is carried out in compliance with current Quebec and Ontario regulations and standards.",
          features: [
            "New constructions",
            "Structural drawings (modifications, renovations, expansions)",
            "Compliance with regulations and standards",
            "Quebec and Ontario accredited engineer with professional seal",
            "Collaboration with contractors, architects, and homeowners",
            "Focus on safety, durability, and efficiency"
          ]
        },
        estimation: {
          title: "Structural Estimation",
          shortDesc: "Precise estimates through our expertise and BIM technology",
          fullDesc: "We offer specialized expertise in building estimation, whether for new construction, renovations, or expansions. Our approach combines field experience and cutting-edge technologies.",
          features: [
            "Building estimation expertise (new, renovation, expansion)",
            "Detailed quantity analysis",
            "Use of BIM technologies for increased precision",
            "Reliable, realistic estimates adapted to site reality"
          ]
        },
        management: {
          title: "Project Management",
          shortDesc: "Complete support from planning to delivery",
          fullDesc: "Our project management service supports you from A to Z in completing your work. We ensure coordination, monitoring, and transparent communication with all stakeholders.",
          features: [
            "Complete A to Z support",
            "Planning, coordination, work monitoring",
            "Subcontractor, cost, and schedule management",
            "Transparent communication with all parties"
          ]
        },
        bim: {
          title: "3D Modeling (BIM)",
          shortDesc: "Precise 3D models for optimal coordination",
          fullDesc: "BIM (Building Information Modeling) allows us to create precise and realistic 3D models, facilitating interdisciplinary coordination and conflict detection.",
          features: [
            "Precise and realistic 3D models",
            "Interdisciplinary coordination",
            "Clash detection",
            "Reduction of unforeseen events and improved estimates",
            "Compliance with Canadian standards"
          ]
        },
        allinone: {
          title: "All-in-One Solution",
          shortDesc: "One contact for all your engineering needs",
          fullDesc: "Our all-in-one solution combines design, estimation, and project management in an integrated service. One point of contact for a seamless experience, from first sketch to key handover.",
          features: [
            "Plan design",
            "Detailed estimation",
            "Complete project management",
            "Single point of contact",
            "Time savings, risk reduction, and superior quality"
          ]
        }
      },
      process: {
        title: "A Proven Process",
        subtitle: "From initial consultation to final delivery, we support you every step of the way.",
        steps: [
          { step: "01", title: "Consultation", desc: "Analysis of your needs and objectives" },
          { step: "02", title: "Design", desc: "Development of plans and models" },
          { step: "03", title: "Validation", desc: "Technical review and approval" },
          { step: "04", title: "Delivery", desc: "Sealed final documents" }
        ]
      }
    },
    whyUs: {
      sectionTitle: "Our Strengths",
      title: "Why Choose Us?",
      subtitle: "D&J Engineering stands out through its expertise, rigor, and commitment to excellence.",
      items: {
        expertise: {
          title: "Expertise and Innovation",
          description: "We use cutting-edge technologies, including BIM, to offer innovative and precise solutions."
        },
        quality: {
          title: "Impeccable Quality",
          description: "Each project is carried out with the utmost rigor, respecting Canadian and Quebec standards."
        },
        support: {
          title: "Personalized Support",
          description: "We adapt our services to your specific needs and support you at every step."
        },
        deadline: {
          title: "Meeting Deadlines",
          description: "We commit to respecting agreed schedules and budgets."
        },
        professionalism: {
          title: "Professionalism and Rigor",
          description: "Our team of accredited engineers guarantees professional and compliant work."
        },
        trust: {
          title: "Trusted Partner",
          description: "We build lasting relationships based on trust and transparency."
        }
      }
    },
    cta: {
      title: "Ready to Start Your Project?",
      subtitle: "Contact us for a free consultation",
      button: "Contact Us"
    },
    aboutPage: {
      badge: "Who We Are",
      title: "About Us",
      subtitle: "Discover the team behind D&J Engineering and our commitment to excellence in structural engineering.",
      identityTitle: "Our Identity",
      teamTitle: "A Versatile and Dedicated Team",
      teamIntro: "D&J Engineering brings together a multidisciplinary team passionate about excellence in structural engineering. Our strength lies in the complementarity of our skills and our culture of collaboration.",
      values: [
        "Continuous innovation and adoption of best practices",
        "Excellence and rigor in every project",
        "Commitment to quality and safety",
        "Transparent communication with our clients"
      ],
      meetTeam: "Meet Our Team",
      missionTitle: "Our Mission",
      missionContent: "Transform ideas into solid and durable structures. We bring our precision, transparency, and passion for detail to every project. Through rigorous technical analysis, precise estimation thanks to our experience and BIM, and proactive project management, we position ourselves as your true partner for success.",
      valuesSection: {
        title: "What Guides Us",
        precision: {
          title: "Precision",
          desc: "Every calculation, every drawing is made with the utmost rigor to guarantee the safety and compliance of your projects."
        },
        transparency: {
          title: "Transparency",
          desc: "Clear and honest communication at every stage of the project. You are informed and involved in all decisions."
        },
        passion: {
          title: "Passion",
          desc: "Our passion for engineering drives us to always seek the best solutions and exceed expectations."
        }
      }
    },
    teamPage: {
      badge: "Our Team",
      title: "The D&J Engineering Team",
      subtitle: "Passionate and experienced professionals dedicated to excellence in structural engineering.",
      introTitle: "A Versatile and Dedicated Team",
      introDesc: "Our multidisciplinary team combines technical expertise, leadership, and passion to deliver exceptional projects. The complementarity of our skills is our greatest strength.",
      ctaTitle: "Let's Work Together on Your Next Project",
      ctaSubtitle: "Our team is ready to support you in achieving your construction projects.",
      members: {
        david: {
          name: "David Aluma",
          role: "CEO & Founder",
          title: "Eng.",
          specialty: "Civil Engineer",
          description: "Visionary and driving force of D&J Engineering, David brings his expertise in design, estimation, and structural drawing. His leadership and technical rigor have forged the firm's reputation for excellence.",
          expertise: ["Structural Design", "Estimation", "Structural Drawing", "Strategic Leadership"]
        },
        joel: {
          name: "Joël Padingani Ing.",
          role: "Vice President & Partner",
          title: "Eng.",
          specialty: "Civil Engineer",
          description: "Strategic pillar of D&J Engineering, Joël excels in planning and project management. His leadership and efficiency ensure the success of every mandate.",
          expertise: ["Project Management", "Planning", "Coordination", "Leadership"]
        },
        leonard: {
          name: "Léonard Ma Gloire Bila",
          role: "Principal Consultant & Construction Expert",
          title: "",
          specialty: "Construction Expert",
          description: "With over 15 years of experience in construction, Léonard brings rare expertise and strategic vision forged in the field. A true reference in project management and cost analysis, he combines technical know-how, rigor, and inspiring leadership.",
          expertise: ["Project Management", "Cost Analysis", "Site Supervision", "15+ Years Experience"]
        }
      }
    },
    contactPage: {
      badge: "Contact Us",
      title: "Let's Talk About Your Project",
      subtitle: "Our team is here to answer your questions and support you in your projects.",
      infoTitle: "Our Contact Information",
      infoDesc: "Don't hesitate to contact us for any questions or quote requests. We will respond as soon as possible.",
      formTitle: "Send Us a Message",
      formDesc: "Fill out the form below and we'll get back to you quickly.",
      labels: {
        name: "Full Name",
        email: "Email",
        phone: "Phone",
        subject: "Subject",
        message: "Message"
      },
      placeholders: {
        name: "Your name",
        email: "your@email.com",
        phone: "(514) 555-0123",
        subject: "Subject of your message",
        message: "Describe your project or question..."
      },
      submit: "Send Message",
      success: {
        title: "Message Sent!",
        desc: "Thank you for your message. Our team will contact you as soon as possible."
      },
      details: {
        phone: "Phone",
        email: "Email",
        location: "Location",
        hours: "Business Hours",
        hoursValue: "Mon - Fri: 8:00 AM - 5:00 PM"
      }
    },
    footer: {
      description: "Engineering firm specializing in building structures in Quebec and Ontario.",
      services: "Our Services",
      navigation: "Navigation",
      contact: "Contact",
      rights: "All rights reserved.",
      oiqMember: "Engineers members of the Ordre des ingénieurs du Québec and Ontario",
      certification: "Professional Certification"
    }
  }
};

export default LanguageContext;
